import { pushLayer } from "../history_stack";
import { APP_SETTINGS } from "../../shared/app_settings";
import { CHANNELS } from "../../shared/channels";
import { CharacterSheet, OcDraft, CharacterImportResult, OcIndexStatus, PictureSeen } from "../../shared/types";
import { OC_FIELDS, OC_GROUPS } from "../../shared/oc_fields";
import { call, listen, mediaSrc } from "../bridge";
import { byId, cloneTemplate, fillFields, flashToast, renderList } from "../dom";
import { t } from "../i18n";
import { amOwner, canUse, myId } from "../me";
import { onViewHidden, onViewShown } from "../navigation";
import { debounce, matchesSearch, shrinkImage } from "../utils";

// character sheets: a searchable grid filtered by world, a lightbox with the whole file, and a full editor

let sheets: CharacterSheet[] = [];
let editing = "";
let shown: CharacterSheet | null = null;
let world = "all";
// the world chip row's own pick for the characters this person owns, not a world name
const MINE = "@mine";
let pickedUrl = "";
// the picture shown big in the viewer, "" for the sheet picture
let showing = "";
let indexStatus: OcIndexStatus | null = null;

const GROUP_LABELS: Record<string, string> = {
  basics: "basics", favourites: "favourites", likes: "likes and goals", looks: "looks",
  identity: "identity", sexuality: "sexuality", story: "story", fractal: "for chi"
};

// fields that are a whole section on their own, the section title is their label
const SECTION_FIELDS = new Set(["story", "notes"]);
// shorter labels for the viewer, where the section already says whose they are
const VIEW_LABELS: Record<string, string> = { sexuality: "orientation", sexTrivia: "trivia", food: "food" };

function listOf(value: string): HTMLElement {
  const list = document.createElement("ul");
  list.className = "oc-list";
  list.append(...value.trim().split("\n").map((line) => line.replace(/^\s*[-•*]\s*/, "").trim()).filter(Boolean).map((line) => {
    const item = document.createElement("li");
    item.append(...keyed(line));
    return item;
  }));
  return list;
}

// a "Info: ..." line keeps its label but reads as a label, not as part of the sentence
function keyed(line: string): Node[] {
  const match = /^([A-Za-z][A-Za-z '/&-]{0,24}?)\s*:\s+(\S.*)$/.exec(line);
  if (!match) return [document.createTextNode(line)];
  const key = document.createElement("b");
  key.className = "oc-key";
  key.textContent = match[1];
  return [key, document.createTextNode(match[2])];
}

// long text keeps its paragraphs, a blank line starts a new one
function textOf(value: string): HTMLElement {
  const host = document.createElement("div");
  host.className = "oc-text";
  host.append(...value.trim().split(/\n\s*\n/).map((chunk) => {
    const paragraph = document.createElement("p");
    chunk.split("\n").forEach((line, index) => {
      if (index) paragraph.append(document.createElement("br"));
      paragraph.append(...keyed(line.trim()));
    });
    return paragraph;
  }));
  return host;
}

// the editor is built once from the field list so new fields need no markup
function buildFields(): void {
  const host = byId("oc-fields");
  host.replaceChildren(...OC_GROUPS.map((group) => {
    const fold = document.createElement("details");
    fold.className = "oc-group";
    fold.open = group === "basics" || group === "looks";
    const summary = document.createElement("summary");
    summary.textContent = t(GROUP_LABELS[group]);
    fold.append(summary);
    for (const field of OC_FIELDS.filter((item) => item.group === group)) {
      const label = document.createElement("label");
      label.className = "field";
      label.textContent = field.kind === "list" ? t("{label}, one per line", { label: t(field.label) }) : t(field.label);
      const input = field.kind === "line" ? document.createElement("input") : document.createElement("textarea");
      input.dataset.key = field.key;
      if (input instanceof HTMLTextAreaElement) input.rows = field.kind === "text" ? 5 : 3;
      else input.autocomplete = "off";
      label.append(input);
      fold.append(label);
    }
    return fold;
  }));
}

function fieldInputs(): Array<HTMLInputElement | HTMLTextAreaElement> {
  return [...byId("oc-fields").querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("[data-key]")];
}

// the editor shows the current picture, or the one just picked before it is saved
function showPicture(src: string, picked: boolean): void {
  const preview = byId<HTMLImageElement>("oc-image-preview");
  if (pickedUrl) URL.revokeObjectURL(pickedUrl);
  pickedUrl = picked ? src : "";
  preview.hidden = !src;
  if (src) preview.src = src;
  else preview.removeAttribute("src");
  byId("oc-image-label").textContent = src ? t("change picture") : t("sheet picture");
}

function openEditor(sheet: CharacterSheet | null): void {
  editing = sheet?.id || "";
  byId("oc-form-title").textContent = sheet ? t("edit {name}", { name: sheet.name }) : t("new character");
  byId<HTMLInputElement>("oc-name").value = sheet?.name || "";
  byId<HTMLInputElement>("oc-tags").value = sheet?.tags.join(", ") || "";
  // a new character starts as the maker's own, untick it for a general chix one
  byId<HTMLInputElement>("oc-mine").checked = sheet ? isMine(sheet) : true;
  byId<HTMLInputElement>("oc-image").value = "";
  showPicture(sheet?.imageUrl ? mediaSrc(sheet.imageUrl) : "", false);
  for (const input of fieldInputs()) input.value = sheet ? String(sheet[input.dataset.key as keyof CharacterSheet] ?? "") : "";
  byId("oc-editor").hidden = false;
  byId<HTMLInputElement>("oc-name").focus();
}

function closeEditor(): void {
  editing = "";
  showPicture("", false);
  byId("oc-editor").hidden = true;
}

function isMine(sheet: CharacterSheet): boolean {
  return myId() ? sheet.owners.includes(myId()) : amOwner() && Boolean(sheet.fromOwner);
}

function worldOf(sheet: CharacterSheet): string {
  return sheet.world.trim() || "unsorted";
}

function visible(): CharacterSheet[] {
  const search = byId<HTMLInputElement>("oc-search").value;
  return sheets.filter((sheet) => {
    if (world === MINE ? !isMine(sheet) : world !== "all" && worldOf(sheet) !== world) return false;
    if (!search.trim()) return true;
    return matchesSearch(search, sheet.name, sheet.tags.join(" "), sheet.aliases.join(" "), ...OC_FIELDS.map((field) => sheet[field.key]));
  });
}

function paintWorlds(): void {
  const counts = new Map<string, number>();
  sheets.forEach((sheet) => counts.set(worldOf(sheet), (counts.get(worldOf(sheet)) || 0) + 1));
  const names = ["all", MINE, ...[...counts.keys()].sort()];
  byId("oc-worlds").replaceChildren(...names.map((name) => {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "chip";
    chip.dataset.world = name;
    chip.classList.toggle("active", name === world);
    if (name === "all") chip.textContent = t("all {n}", { n: sheets.length });
    else if (name === MINE) chip.textContent = t("mine {n}", { n: sheets.filter(isMine).length });
    else chip.textContent = `${name === "unsorted" ? t("unsorted") : name} ${counts.get(name)}`;
    return chip;
  }));
}

function render(): void {
  const list = visible();
  renderList(byId("oc-grid"), list, (sheet) => {
    const tile = fillFields(cloneTemplate("template-oc"), {
      name: sheet.name,
      look: [sheet.race, sheet.world].filter(Boolean).join(" · ") || sheet.look
    });
    const image = tile.querySelector("img");
    if (image && sheet.imageUrl) image.src = mediaSrc(sheet.imageUrl);
    else {
      image?.remove();
      const media = tile.querySelector(".tile-media");
      if (media) media.textContent = sheet.name.slice(0, 1).toUpperCase();
    }
    if (isMine(sheet)) {
      const badge = document.createElement("span");
      badge.className = "tile-badge yours";
      badge.textContent = t("yours");
      tile.append(badge);
    }
    if (sheet.rating.toLowerCase() === "nsfw") {
      const badge = document.createElement("span");
      badge.className = "tile-badge";
      badge.textContent = "nsfw";
      tile.append(badge);
    }
    tile.addEventListener("click", () => openSheet(sheet));
    return tile;
  });
  byId("oc-empty").hidden = list.length > 0;
  byId("oc-count").textContent = list.length === sheets.length ? `${sheets.length}` : t("{shown} of {total}", { shown: list.length, total: sheets.length });
  paintWorlds();
}

function load(next: CharacterSheet[]): void {
  sheets = [...next].sort((a, b) => a.name.localeCompare(b.name));
  render();
  if (shown) {
    const fresh = sheets.find((sheet) => sheet.id === shown?.id);
    // chi writing up pictures refreshes the sheets often, the picture being looked at stays up
    const keep = showing;
    if (fresh) openSheet(fresh);
    if (fresh && keep) byId("oc-viewer-gallery").querySelector<HTMLButtonElement>(`button[data-file="${CSS.escape(keep)}"]`)?.click();
  }
}

// what chi wrote about the picture shown big, once she has looked at it
function paintSeen(seen: PictureSeen | undefined): void {
  byId("oc-viewer-seen").hidden = !seen;
  byId("oc-viewer-seen-text").textContent = seen?.caption || "";
  byId("oc-viewer-seen-tags").replaceChildren(...(seen?.tags || []).map((tag) => {
    const chip = document.createElement("span");
    chip.className = "chip";
    chip.textContent = tag.replace(/_/g, " ");
    return chip;
  }));
}

function money(usd: number): string {
  return usd < 0.01 ? t("under a cent") : `$${usd.toFixed(2)}`;
}

function picturesLeft(sheet: CharacterSheet): number {
  return (sheet.imageFile && !sheet.imageSeen ? 1 : 0) + sheet.gallery.filter((picture) => !picture.seen).length;
}

function paintIndex(): void {
  const key = byId<HTMLButtonElement>("oc-index");
  const status = indexStatus;
  key.hidden = !status || !status.total || !canUse("ocs", "edit");
  if (!status) return;
  byId("oc-index-label").textContent = status.running ? t("chi looking {done}/{total}", { done: status.done, total: status.total }) : status.left ? t("chi look {done}/{total}", { done: status.done, total: status.total }) : t("chi looked");
}

// the backlog goes a bounded slice per press, with a rough price first since it runs on Fractal's key
async function indexBacklog(): Promise<void> {
  try {
    const status = await call<OcIndexStatus>(CHANNELS.ocsIndexStatus);
    indexStatus = status;
    paintIndex();
    if (!status.left) return flashToast(t("chi has looked at every character picture"));
    const count = Math.min(status.left, status.runMax);
    const rest = status.left > count ? ` ${t("{n} more wait for another press.", { n: status.left - count })}` : "";
    if (!confirm(t("chi has looked at {done} of {total} character pictures. look at the next {count} for about {price}? it goes through the half price batch, so it can take a while.", { done: status.done, total: status.total, count, price: money(count * status.usdPerPicture) }) + rest)) return;
    indexStatus = await call<OcIndexStatus>(CHANNELS.ocsIndex, { all: true });
    paintIndex();
    flashToast(t("chi is looking at {count} pictures", { count }));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not start chi"));
  }
}

// one character's pictures straight away, or all of them again when she has seen them all
async function indexSheet(): Promise<void> {
  if (!shown) return;
  const sheet = shown;
  const total = (sheet.imageFile ? 1 : 0) + sheet.gallery.length;
  const left = picturesLeft(sheet);
  const count = left || total;
  // a few go live at full price, more go through the batch at half
  const usd = count * APP_SETTINGS.ocTagUsdPerPicture * (count <= APP_SETTINGS.ocTagLiveMax ? 2 : 1);
  const ask = left
    ? left === 1
      ? t("chi looks at 1 picture of {name} she hasn't seen, about {price}?", { name: sheet.name, price: money(usd) })
      : t("chi looks at {count} pictures of {name} she hasn't seen, about {price}?", { count: left, name: sheet.name, price: money(usd) })
    : t("chi has seen all {total} pictures of {name}. look at them again from scratch, about {price}?", { total, name: sheet.name, price: money(usd) });
  if (!total || !confirm(ask)) return;
  try {
    indexStatus = await call<OcIndexStatus>(CHANNELS.ocsIndex, { id: sheet.id, redo: !left });
    paintIndex();
    flashToast(t("chi is looking at {name}", { name: sheet.name }));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not start chi"));
  }
}

// the sheet picture and the rest of the gallery as a strip, tap one to see it big
function paintGallery(sheet: CharacterSheet): void {
  const host = byId("oc-viewer-gallery");
  const cover = byId<HTMLButtonElement>("oc-viewer-cover");
  const pictures = [{ file: "", url: sheet.imageUrl, caption: sheet.imageCaption || t("sheet picture"), seen: sheet.imageSeen }, ...sheet.gallery].filter((picture) => picture.url);
  paintSeen(sheet.imageSeen);
  host.hidden = pictures.length < 2;
  cover.hidden = true;
  cover.dataset.file = "";
  showing = "";
  byId("oc-viewer-drop").hidden = !sheet.imageUrl || !canUse("ocs", "edit");
  host.replaceChildren(...(pictures.length < 2 ? [] : pictures).map((picture, index) => {
    const button = document.createElement("button");
    button.type = "button";
    button.title = picture.caption;
    button.dataset.file = picture.file;
    button.setAttribute("aria-label", picture.caption || t("picture {n}", { n: index + 1 }));
    button.classList.toggle("active", index === 0);
    const thumb = document.createElement("img");
    thumb.loading = "lazy";
    thumb.alt = "";
    thumb.src = mediaSrc(picture.url);
    button.append(thumb);
    button.addEventListener("click", () => {
      const image = byId<HTMLImageElement>("oc-viewer-image");
      image.hidden = false;
      byId("oc-viewer-initial").hidden = true;
      image.src = mediaSrc(picture.url);
      host.querySelectorAll("button").forEach((item) => item.classList.toggle("active", item === button));
      // a gallery picture can become the one on the character's tile
      cover.hidden = !picture.file || !canUse("ocs", "edit");
      cover.dataset.file = picture.file;
      showing = picture.file;
      paintSeen(picture.seen);
    });
    return button;
  }));
}

// pictures picked from the phone or computer go into the gallery, or become the sheet picture when there is none
async function addPictures(files: File[]): Promise<void> {
  if (!shown || !files.length) return;
  const id = shown.id;
  const before = new Set([shown.imageFile, ...shown.gallery.map((picture) => picture.file)]);
  flashToast(files.length === 1 ? t("adding 1 picture…") : t("adding {n} pictures…", { n: files.length }));
  try {
    for (const file of files) {
      const data = await shrinkImage(file, APP_SETTINGS.galleryImageMaxPx);
      const sheet = sheets.find((item) => item.id === id);
      if (!sheet) return;
      const caption = file.name.replace(/\.[^.]+$/, "");
      load(await call<CharacterSheet[]>(CHANNELS.ocsSave, sheet.imageFile
        ? { id, name: sheet.name, galleryData: [{ data, caption }] }
        : { id, name: sheet.name, imageData: data, imageCaption: caption }));
    }
    flashToast(files.length === 1 ? t("picture added") : t("{n} pictures added", { n: files.length }));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not add the picture"));
  }
  // chi looks at pictures added by hand straight away. no key or no budget just leaves them for later
  const sheet = sheets.find((item) => item.id === id);
  const added = sheet ? [sheet.imageFile, ...sheet.gallery.map((picture) => picture.file)].filter((file) => file && !before.has(file)) : [];
  if (added.length) void call<OcIndexStatus>(CHANNELS.ocsIndex, { id, files: added }).catch(() => undefined);
}

async function dropPicture(): Promise<void> {
  if (!shown || !confirm(t("take this picture off the character?"))) return;
  try {
    load(await call<CharacterSheet[]>(CHANNELS.ocsRemovePicture, shown.id, showing));
    flashToast(t("picture removed"));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not remove the picture"));
  }
}

async function useAsPicture(): Promise<void> {
  const file = byId("oc-viewer-cover").dataset.file;
  if (!shown || !file) return;
  try {
    load(await call<CharacterSheet[]>(CHANNELS.ocsSetPicture, shown.id, file));
    flashToast(t("sheet picture changed"));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not change the picture"));
  }
}

// both imports run on chi's server and update the sheets chi already has
async function runImport(buttonId: string, channel: string, label: string): Promise<void> {
  const button = byId<HTMLButtonElement>(buttonId);
  button.disabled = true;
  flashToast(t("fetching characters from {source}…", { source: label }));
  try {
    const result = await call<CharacterImportResult>(channel);
    load(await call<CharacterSheet[]>(CHANNELS.ocsList));
    const parts = [t("{n} found", { n: result.found }), t("{n} added", { n: result.added.length }), t("{n} updated", { n: result.updated.length })];
    if (result.merged.length) parts.push(t("{n} duplicates merged", { n: result.merged.length }));
    if (result.failed.length) parts.push(t("{n} failed", { n: result.failed.length }));
    flashToast(`${label}: ${parts.join(", ")}`);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("{source} import failed", { source: label }));
  } finally {
    button.disabled = false;
  }
}

// who owns the character, and the key that adds or takes off the current person
function paintOwners(sheet: CharacterSheet): void {
  const mine = isMine(sheet);
  const host = byId("oc-viewer-owners");
  host.hidden = !sheet.owners.length;
  const label = document.createElement("span");
  label.className = "field-label";
  label.textContent = t("owners");
  host.replaceChildren(label, ...sheet.owners.map((id, index) => {
    const chip = document.createElement("span");
    chip.className = "oc-owner";
    // the desktop app has no id to match, it shows the name
    const you = Boolean(myId()) && id === myId();
    chip.classList.toggle("me", you);
    chip.textContent = you ? t("you") : sheet.ownerNames?.[index] || id;
    return chip;
  }));
  const key = byId("oc-viewer-own");
  key.classList.toggle("active", mine);
  key.setAttribute("aria-pressed", String(mine));
  key.hidden = !canUse("ocs");
}

async function toggleOwner(): Promise<void> {
  if (!shown) return;
  const mine = !isMine(shown);
  try {
    load(await call<CharacterSheet[]>(CHANNELS.ocsOwn, shown.id, mine));
    flashToast(mine ? t("{name} is yours now", { name: shown?.name || t("the sheet") }) : t("{name} is not yours any more", { name: shown?.name || t("the sheet") }));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not change the owner"));
  }
}

// tap a character to read the whole file big, edit from there
let releaseLayer: (() => void) | null = null;

function openSheet(sheet: CharacterSheet): void {
  shown = sheet;
  if (byId("oc-viewer").hidden) {
    releaseLayer = pushLayer(() => {
      releaseLayer = null;
      closeSheet();
    });
  }
  const image = byId<HTMLImageElement>("oc-viewer-image");
  const initial = byId("oc-viewer-initial");
  image.hidden = !sheet.imageUrl;
  initial.hidden = Boolean(sheet.imageUrl);
  if (sheet.imageUrl) image.src = mediaSrc(sheet.imageUrl);
  else {
    image.removeAttribute("src");
    initial.textContent = sheet.name.slice(0, 1).toUpperCase();
  }
  paintGallery(sheet);
  byId("oc-viewer-name").textContent = sheet.name;
  byId("oc-viewer-world").textContent = [sheet.world, sheet.rating, sheet.aliases.length ? t("aka {names}", { names: sheet.aliases.join(", ") }) : ""].filter(Boolean).join(" · ") || t("character file");
  paintOwners(sheet);
  byId("oc-viewer-merge").hidden = !canUse("ocs", "edit");
  byId("oc-viewer-index").hidden = !canUse("ocs", "edit") || (!sheet.imageFile && !sheet.gallery.length);
  byId("oc-viewer-add").hidden = !canUse("ocs", "edit");
  const facts = byId("oc-viewer-facts");
  let number = 0;
  facts.replaceChildren(...OC_GROUPS.flatMap((group) => {
    const fields = OC_FIELDS.filter((field) => field.group === group && field.key !== "world" && field.key !== "rating" && sheet[field.key].trim());
    if (!fields.length) return [];
    number += 1;
    const section = document.createElement("section");
    section.className = "oc-section";
    const title = document.createElement("h2");
    const channel = document.createElement("span");
    channel.className = "oc-section-no";
    channel.textContent = String(number).padStart(2, "0");
    title.append(channel, t(GROUP_LABELS[group]));
    section.append(title);
    // short one-line answers read as a spec table, the rest as labelled blocks
    const short = fields.filter((field) => field.kind === "line" && sheet[field.key].trim().length <= 60 && !sheet[field.key].includes("\n"));
    if (short.length) {
      const table = document.createElement("dl");
      table.className = "oc-spec";
      for (const field of short) {
        const key = document.createElement("dt");
        key.textContent = t(VIEW_LABELS[field.key] || field.label);
        const value = document.createElement("dd");
        value.textContent = sheet[field.key].trim();
        table.append(key, value);
      }
      section.append(table);
    }
    for (const field of fields.filter((item) => !short.includes(item))) {
      const block = document.createElement("div");
      block.className = "oc-block";
      // the section title already names a field that is the whole section (story, for chi)
      if (!SECTION_FIELDS.has(field.key)) {
        const label = document.createElement("span");
        label.className = "field-label";
        label.textContent = t(VIEW_LABELS[field.key] || field.label);
        block.append(label);
      }
      block.append(field.kind === "list" ? listOf(sheet[field.key]) : textOf(sheet[field.key]));
      section.append(block);
    }
    return [section];
  }));
  byId("oc-viewer-tags").replaceChildren(...sheet.tags.map((tag) => {
    const chip = document.createElement("span");
    chip.className = "chip";
    chip.textContent = tag;
    return chip;
  }));
  byId("oc-viewer").hidden = false;
}

// the other sheets, likeliest first: names that start alike (Cora, Coraline), then the rest by name
function paintMergeList(): void {
  if (!shown) return;
  const me = shown.name.toLowerCase();
  const search = byId<HTMLInputElement>("oc-merge-search").value;
  const close = (sheet: CharacterSheet) => {
    const other = sheet.name.toLowerCase();
    return other.startsWith(me.slice(0, 3)) || me.startsWith(other.slice(0, 3)) ? 0 : 1;
  };
  const others = sheets
    .filter((sheet) => sheet.id !== shown?.id && (!search.trim() || matchesSearch(search, sheet.name, sheet.aliases.join(" "))))
    .sort((a, b) => close(a) - close(b) || a.name.localeCompare(b.name));
  byId("oc-merge-list").replaceChildren(...others.map((sheet) => {
    const button = document.createElement("button");
    button.type = "button";
    if (sheet.imageUrl) {
      const thumb = document.createElement("img");
      thumb.loading = "lazy";
      thumb.alt = "";
      thumb.src = mediaSrc(sheet.imageUrl);
      button.append(thumb);
    } else {
      const initial = document.createElement("span");
      initial.className = "oc-merge-initial";
      initial.textContent = sheet.name.slice(0, 1).toUpperCase();
      button.append(initial);
    }
    const text = document.createElement("span");
    text.textContent = sheet.name;
    const more = document.createElement("small");
    more.textContent = [sheet.world, sheet.race, sheet.gallery.length ? t("{n} pictures", { n: sheet.gallery.length + (sheet.imageUrl ? 1 : 0) }) : ""].filter(Boolean).join(" · ");
    text.append(more);
    button.append(text);
    button.addEventListener("click", () => void mergeIn(sheet));
    return button;
  }));
}

function openMerge(): void {
  if (!shown) return;
  byId("oc-merge-title").textContent = t("merge into {name}", { name: shown.name });
  byId<HTMLInputElement>("oc-merge-search").value = "";
  paintMergeList();
  byId("oc-merge").hidden = false;
}

function closeMerge(): void {
  byId("oc-merge").hidden = true;
}

async function mergeIn(other: CharacterSheet): Promise<void> {
  if (!shown || !confirm(t("merge {other} into {name}? {other}'s sheet goes away and its pictures move to {name}.", { other: other.name, name: shown.name }))) return;
  try {
    load(await call<CharacterSheet[]>(CHANNELS.ocsMerge, shown.id, other.id));
    closeMerge();
    flashToast(t("{other} merged into {name}", { other: other.name, name: shown?.name || t("the sheet") }));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not merge"));
  }
}

function closeSheet(): void {
  shown = null;
  byId("oc-viewer").hidden = true;
  const release = releaseLayer;
  releaseLayer = null;
  release?.();
}

async function save(event: Event): Promise<void> {
  event.preventDefault();
  const file = byId<HTMLInputElement>("oc-image").files?.[0];
  const draft: OcDraft = {
    id: editing,
    name: byId<HTMLInputElement>("oc-name").value,
    tags: byId<HTMLInputElement>("oc-tags").value,
    mine: byId<HTMLInputElement>("oc-mine").checked,
    imageData: file ? await shrinkImage(file, APP_SETTINGS.sheetImageMaxPx) : ""
  };
  for (const input of fieldInputs()) (draft as Record<string, unknown>)[input.dataset.key || ""] = input.value;
  try {
    load(await call<CharacterSheet[]>(CHANNELS.ocsSave, draft));
    closeEditor();
    flashToast(t("character saved"));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not save"));
  }
}

// opens the character file over whatever view is showing, so chat stays put underneath
export async function openOc(id: string): Promise<void> {
  if (!sheets.some((sheet) => sheet.id === id)) load(await call<CharacterSheet[]>(CHANNELS.ocsList));
  const sheet = sheets.find((item) => item.id === id);
  if (sheet) openSheet(sheet);
}

export async function setupOcsView(): Promise<void> {
  buildFields();
  // the viewer and its sheets live on body like the ref lightbox, so a character opened from chat
  // shows over the chat instead of inside the hidden characters view
  document.body.append(byId("oc-viewer"), byId("oc-editor"), byId("oc-merge"));
  onViewHidden("ocs", () => {
    closeMerge();
    closeEditor();
    closeSheet();
  });
  byId("oc-form").addEventListener("submit", (event) => void save(event));
  byId("oc-image").addEventListener("change", (event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) showPicture(URL.createObjectURL(file), true);
  });
  byId("oc-new").addEventListener("click", () => openEditor(null));
  byId("oc-import-zirk").addEventListener("click", () => void runImport("oc-import-zirk", CHANNELS.ocsImportZirk, "zirk.eu"));
  byId("oc-import-discord").addEventListener("click", () => void runImport("oc-import-discord", CHANNELS.ocsImportDiscord, "discord"));
  byId("oc-viewer-own").addEventListener("click", () => void toggleOwner());
  byId("oc-viewer-cover").addEventListener("click", () => void useAsPicture());
  byId("oc-viewer-merge").addEventListener("click", openMerge);
  byId("oc-viewer-index").addEventListener("click", () => void indexSheet());
  byId("oc-index").addEventListener("click", () => void indexBacklog());
  byId("oc-viewer-drop").addEventListener("click", () => void dropPicture());
  byId("oc-viewer-files").addEventListener("change", (event) => {
    const input = event.target as HTMLInputElement;
    const files = [...input.files || []];
    input.value = "";
    void addPictures(files);
  });
  byId("oc-merge-close").addEventListener("click", closeMerge);
  byId("oc-merge-search").addEventListener("input", debounce(paintMergeList, 120));
  byId("oc-clear").addEventListener("click", closeEditor);
  byId("oc-cancel").addEventListener("click", closeEditor);
  byId("oc-search").addEventListener("input", debounce(render, 120));
  byId("oc-worlds").addEventListener("click", (event) => {
    const chip = (event.target as Element).closest<HTMLElement>("[data-world]");
    if (!chip) return;
    world = chip.dataset.world || "all";
    render();
  });
  byId("oc-viewer-close").addEventListener("click", closeSheet);
  byId("oc-viewer-stage").addEventListener("click", (event) => {
    if ((event.target as HTMLElement).id === "oc-viewer-stage") closeSheet();
  });
  byId("oc-viewer-edit").addEventListener("click", () => {
    if (!shown) return;
    const sheet = shown;
    closeSheet();
    openEditor(sheet);
  });
  byId("oc-viewer-delete").addEventListener("click", async () => {
    if (!shown || !confirm(t("delete {name}?", { name: shown.name }))) return;
    const id = shown.id;
    closeSheet();
    load(await call<CharacterSheet[]>(CHANNELS.ocsRemove, id));
  });
  window.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    if (!byId("oc-merge").hidden) closeMerge();
    else if (!byId("oc-editor").hidden) closeEditor();
    else if (shown) closeSheet();
  });
  listen<CharacterSheet[]>(CHANNELS.ocsUpdated, (next) => load(next));
  listen<OcIndexStatus>(CHANNELS.ocsIndexUpdated, (next) => {
    indexStatus = next;
    paintIndex();
  });
  onViewShown("ocs", async () => {
    byId("oc-import-zirk").hidden = !canUse("ocs", "edit");
    byId("oc-import-discord").hidden = !canUse("ocs", "edit");
    load(await call<CharacterSheet[]>(CHANNELS.ocsList));
    indexStatus = await call<OcIndexStatus>(CHANNELS.ocsIndexStatus).catch(() => null);
    paintIndex();
  });
}
